/**
 * Zones (CR 106-109; research 01 §5; docs/design/03 §3.2).
 *
 * Board zones: each player's Base (with its rune area), the Battlefield Zone (every battlefield is a
 * location with its own Facedown Zone) and each player's Legend Zone (CR 107). Everything else is a
 * non-board zone: the Chain, and per player the Trash, Champion Zone, Main Deck, Rune Deck, Banishment
 * and Hand (CR 108). An object that moves into or out of a non-board zone becomes a new object (CR 124).
 *
 * The rune area is modelled as its own list (`runes`) for clarity, but it is part of the player's Base:
 * runes live in their controller's base (CR 107.1.c), so `runes` is a board zone at the base location.
 */
import type { BattlefieldId, PlayerId } from './ids';

/** Zones that belong to one player. */
export const PLAYER_ZONE_KINDS = [
  'mainDeck',
  'runeDeck',
  'hand',
  'trash',
  'banishment',
  'champion',
  'legend',
  'base',
  'runes',
] as const;
export type PlayerZoneKind = (typeof PLAYER_ZONE_KINDS)[number];

export const ZONE_KINDS = [...PLAYER_ZONE_KINDS, 'battlefield', 'facedown', 'chain'] as const;
export type ZoneKind = (typeof ZONE_KINDS)[number];

/** Where an object is. Battlefield and facedown zones are per battlefield (CR 107.2-107.3); the chain is shared. */
export type ZoneRef =
  | { kind: PlayerZoneKind; player: PlayerId }
  | { kind: 'battlefield'; battlefield: BattlefieldId }
  | { kind: 'facedown'; battlefield: BattlefieldId }
  | { kind: 'chain' };

/** Privacy of a zone's contents (CR 128.3-128.5). */
export type ZonePrivacy = 'public' | 'private' | 'secret';

/** Rules facts about a zone kind. */
export interface ZoneInfo {
  /** Board zone (CR 107) or not (CR 108). Moving into/out of a non-board zone makes a new object (CR 124). */
  readonly board: boolean;
  /** Whether the order of the list is meaningful to the rules (decks: top = index 0; the chain). */
  readonly ordered: boolean;
  /**
   * Default privacy: public (everyone), private (owner, or the controller for facedown cards), secret
   * (nobody, not even the owner). Objects can be revealed beyond the default (`GameObject.revealedTo`).
   */
  readonly privacy: ZonePrivacy;
  /** Core Rules reference. */
  readonly rule: string;
}

/** The zone table (research 01 §5.1). */
export const ZONE_INFO: Readonly<Record<ZoneKind, ZoneInfo>> = {
  mainDeck: { board: false, ordered: true, privacy: 'secret', rule: 'CR 108.4' },
  runeDeck: { board: false, ordered: true, privacy: 'secret', rule: 'CR 108.5' },
  hand: { board: false, ordered: false, privacy: 'private', rule: 'CR 108.7' },
  trash: { board: false, ordered: false, privacy: 'public', rule: 'CR 108.2' },
  banishment: { board: false, ordered: false, privacy: 'public', rule: 'CR 108.6' },
  champion: { board: false, ordered: false, privacy: 'public', rule: 'CR 108.3' },
  chain: { board: false, ordered: true, privacy: 'public', rule: 'CR 108.1, 330' },
  legend: { board: true, ordered: false, privacy: 'public', rule: 'CR 107.4' },
  base: { board: true, ordered: false, privacy: 'public', rule: 'CR 107.1' },
  runes: { board: true, ordered: false, privacy: 'public', rule: 'CR 107.1.c' },
  battlefield: { board: true, ordered: false, privacy: 'public', rule: 'CR 107.2' },
  facedown: { board: true, ordered: false, privacy: 'private', rule: 'CR 107.3' },
};

/** Deck zones a player can draw, channel, shuffle or look at. */
export type DeckZoneKind = 'mainDeck' | 'runeDeck';

/** Position in an ordered zone: top (index 0, next to draw), bottom (recycle, CR 417), or an index from the top. */
export type DeckPosition = 'top' | 'bottom' | number;

/** Board zone (CR 107) or not (CR 108). */
export function isBoardZone(zone: ZoneRef): boolean {
  return ZONE_INFO[zone.kind].board;
}

/** Whether the zone belongs to one player. */
export function isPlayerZone(zone: ZoneRef): zone is { kind: PlayerZoneKind; player: PlayerId } {
  return 'player' in zone;
}

/** Structural equality of two zone references. */
export function sameZone(a: ZoneRef, b: ZoneRef): boolean {
  if (a.kind !== b.kind) return false;
  if ('player' in a && 'player' in b) return a.player === b.player;
  if ('battlefield' in a && 'battlefield' in b) return a.battlefield === b.battlefield;
  return a.kind === 'chain';
}

/** A stable string key for a zone (`p1:hand`, `bf2:facedown`, `chain`), for maps and logs. */
export function zoneKey(zone: ZoneRef): string {
  if ('player' in zone) return `${zone.player}:${zone.kind}`;
  if ('battlefield' in zone) return `${zone.battlefield}:${zone.kind}`;
  return 'chain';
}

/**
 * The location of an object on the board (CR 146.1, 198): its base, or a battlefield. Rune and facedown
 * zones resolve to their base / battlefield. Non-board zones have no location (`null`).
 */
export type Location = { kind: 'base'; player: PlayerId } | { kind: 'battlefield'; battlefield: BattlefieldId };

/** The board location of a zone, or `null` for non-board zones. */
export function locationOf(zone: ZoneRef): Location | null {
  switch (zone.kind) {
    case 'base':
    case 'runes':
      return { kind: 'base', player: zone.player };
    case 'battlefield':
    case 'facedown':
      return { kind: 'battlefield', battlefield: zone.battlefield };
    case 'mainDeck':
    case 'runeDeck':
    case 'hand':
    case 'trash':
    case 'banishment':
    case 'champion':
    case 'legend':
    case 'chain':
      return null;
  }
}
